import React, { FC } from 'react';
import styles from './styles.module.scss';
import defaultStyles from '../../../css/default.module.scss';
import { Link } from 'react-router-dom';
import { HeroAreaProps, ButtonsProps, SectionProps } from './types';
import { Back } from '../Navigation/Navigation';
import useIsDevice from '../../../hooks/other/useIsDevice';
import Caption from './components/Caption';

export const HeroArea = ({
    title,
    description,
    descriptionJsx,
    buttonText,
    buttonLink,
    buttonLinkExternal,
    buttonOnClick,
    clsOuter = '',
    clsInner = '',
    clsBtn = defaultStyles.btnPrimary,
    fullWidth,
    image,
    imageMobile,
    imageAlt,
    extraImagePadding = false,
    buttonTwoText,
    buttonTwoLink,
    buttonTwoLinkExternal,
    buttonTwoOnClick,
    clsBtnTwo = defaultStyles.btnDark,
    tall = true,
    withBackButton = false,
    leftText = false,
    additionalContent,
    additionalContentInBody = false,
    extraBottomSpace = false,
    onImageClick,
    buttonThreeText,
    buttonThreeLink,
    buttonThreeLinkExternal,
    buttonThreeOnClick,
    clsBtnThree = defaultStyles.btnPrimaryBordered,
    mainContentRight = false,
    smallImage = false,
    checks = [],
    moreDeviceTopSpacing = false,
    tightTopAndBottom = false,
    aboveChecksJsx,
    belowChecksJsx,
    belowButtonsJsx,
    extraTicksMargin = false,
    equalWidthAreas = false,
    caption,
}: HeroAreaProps) => {
    const outerClasses: string = `${styles.heroAreaOuter} ${
        tall ? styles.tall : styles.small
    } ${clsOuter} ${extraBottomSpace ? styles.extraBottomSpace : ''} ${
        tightTopAndBottom ? styles.tightTopAndBottom : ''
    }`;
    const innerClasses: string = `${styles.heroAreaInner} ${styles.specificity} ${
        fullWidth ? styles.fullWidth : ''
    } ${clsInner}`;
    const btnProps: ButtonsProps = {
        buttonLink,
        buttonText,
        clsBtn,
        buttonOnClick,
        buttonTwoLink,
        buttonTwoText,
        clsBtnTwo,
        buttonLinkExternal,
        buttonTwoLinkExternal,
        buttonTwoOnClick,
        buttonThreeLink,
        buttonThreeText,
        clsBtnThree,
        buttonThreeLinkExternal,
        buttonThreeOnClick,
        leftText,
    };
    const hasButtons = btnProps.buttonText || btnProps.buttonTwoText || btnProps.buttonThreeText;
    const paragraphClasses = hasButtons ? '' : styles.noMargin;
    const isMediumViewport = useIsDevice(1000);
    const divClasses = `${styles.textAndButtonContainer} ${
        equalWidthAreas ? styles.equalWidthAreas : ''
    } ${mainContentRight ? styles.mainContentRight : ''} ${leftText && styles.leftText} ${
        moreDeviceTopSpacing ? styles.moreDeviceTopSpacing : ''
    }`;

    return (
        <Section clsOuter={outerClasses} clsInner={innerClasses} tag='section'>
            <div className={divClasses}>
                {title && <h1>{title}</h1>}
                {description && <p className={paragraphClasses}>{description}</p>}
                {descriptionJsx && descriptionJsx}
                {image && (
                    <figure>
                        <picture>
                            <source srcSet={imageMobile || image} media='(max-width: 768px)' />
                            <source srcSet={image} media='(min-width: 769px)' />
                            <img
                                src={image}
                                alt={imageAlt}
                                onClick={onImageClick}
                                className={styles['img-embedded']}
                                // loading='lazy' // do not lazy load images ATF
                                role='button'
                                aria-label={imageAlt}
                            />
                        </picture>
                        {caption && (
                            <Caption text={caption.text} bold={caption.bold} cls={styles.caption} />
                        )}
                    </figure>
                )}
                {!image &&
                    additionalContent &&
                    additionalContentInBody &&
                    isMediumViewport &&
                    additionalContent}
                {aboveChecksJsx && aboveChecksJsx}
                {checks.length > 0 && (
                    <ul
                        className={`${styles['ticks-container']} ${
                            extraTicksMargin ? styles.extraTicksMargin : ''
                        }`}
                    >
                        {checks.map((check) => (
                            <li key={check}>
                                <span>{check}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {belowChecksJsx && belowChecksJsx}
                {hasButtons && <Buttons {...btnProps} />}
                {belowButtonsJsx && belowButtonsJsx}
                {withBackButton && <Back />}
            </div>
            {image && (
                <div
                    className={`${styles.imgWrap} ${
                        extraImagePadding
                            ? mainContentRight
                                ? styles.extraImagePaddingRight
                                : styles.extraImagePaddingLeft
                            : ''
                    } ${smallImage ? styles.smallImage : ''}`}
                >
                    <figure>
                        <picture>
                            <source srcSet={imageMobile || image} media='(max-width: 768px)' />
                            <source srcSet={image} media='(min-width: 769px)' />
                            <img
                                src={image}
                                alt={imageAlt}
                                onClick={onImageClick}
                                className={styles['img-embedded']}
                                // loading='lazy' // do not lazy load images ATF
                                role='button'
                                aria-label={imageAlt}
                            />
                        </picture>
                        {caption && (
                            <Caption text={caption.text} bold={caption.bold} cls={styles.caption} />
                        )}
                    </figure>
                </div>
            )}
            {(!image && additionalContent && !additionalContentInBody && additionalContent) ||
                (!image &&
                    additionalContent &&
                    additionalContentInBody &&
                    !isMediumViewport &&
                    additionalContent)}
        </Section>
    );
};

export const Buttons: FC<ButtonsProps> = ({
    buttonLink,
    buttonText,
    clsBtn,
    buttonTwoLink,
    buttonTwoText,
    buttonOnClick,
    clsBtnTwo,
    buttonLinkExternal,
    buttonTwoLinkExternal,
    buttonTwoOnClick,
    leftText,
    leftButtons = false,
    buttonThreeLink,
    buttonThreeText,
    buttonThreeLinkExternal,
    buttonThreeOnClick,
    clsBtnThree,
}) => {
    const btnOneAnchor = buttonLinkExternal || buttonLink?.[0] === '#';
    const btnTwoAnchor = buttonTwoLinkExternal || buttonTwoLink?.[0] === '#';
    const btnThreeAnchor = buttonThreeLinkExternal || buttonThreeLink?.[0] === '#';
    const containerClasses = `${defaultStyles.buttonsContainer} ${defaultStyles.largeButtons} ${
        leftText ? '' : styles.centralButtons
    } ${leftButtons ? styles.leftButtons : ''}`;

    return (
        <div className={containerClasses}>
            {(buttonLink || buttonOnClick) &&
                (btnOneAnchor ? (
                    <a href={buttonLink} className={clsBtn}>
                        {buttonText}
                    </a>
                ) : buttonOnClick ? (
                    <button onClick={buttonOnClick} className={clsBtn}>
                        {buttonText}
                    </button>
                ) : (
                    <Link to={buttonLink as string} className={clsBtn}>
                        {buttonText}
                    </Link>
                ))}
            {(buttonTwoLink || buttonTwoOnClick) &&
                (btnTwoAnchor ? (
                    <a href={buttonTwoLink} className={clsBtnTwo}>
                        {buttonTwoText}
                    </a>
                ) : buttonTwoOnClick ? (
                    <button onClick={buttonTwoOnClick} className={clsBtnTwo}>
                        {buttonTwoText}
                    </button>
                ) : (
                    <Link to={buttonTwoLink as string} className={clsBtnTwo}>
                        {buttonTwoText}
                    </Link>
                ))}
            {(buttonThreeLink || buttonThreeOnClick) &&
                (btnThreeAnchor ? (
                    <a href={buttonThreeLink} className={clsBtnThree}>
                        {buttonThreeText}
                    </a>
                ) : buttonThreeOnClick ? (
                    <button onClick={buttonThreeOnClick} className={clsBtnThree}>
                        {buttonThreeText}
                    </button>
                ) : (
                    <Link to={buttonThreeLink as string} className={clsBtnThree}>
                        {buttonThreeText}
                    </Link>
                ))}
        </div>
    );
};

export const Section: FC<SectionProps> = ({
    children,
    clsOuter = '',
    clsInner = '',
    patterned,
    tag: Tag = 'section',
    id,
    centralised = false,
    inverse = false,
}) => {
    const outerClasses = `${defaultStyles.outer} ${
        patterned ? defaultStyles.patterned : ''
    } ${clsOuter} ${centralised ? defaultStyles.centralised : ''}`;
    const innerClasses = `${defaultStyles.inner} ${clsInner}`;

    return (
        <>
            {inverse && <section className={styles.hide}></section>}
            <Tag id={id} className={outerClasses}>
                <div className={innerClasses}>{children}</div>
            </Tag>
        </>
    );
};
